import React, { useState, useEffect } from 'react';
import { get, map } from 'lodash';
import MessageRenderer from 'components/MessageRenderer';
import styled from 'styled-components';
import { Mic, VolumeUp, Videocam, Close, Check } from '@mui/icons-material';
import { handleGetTranscripts, handleAudioTranscript } from 'store/submissions/thunks';
import {
  lookupTranscript,
  resolveMediaUrl,
  resolveGroupStemAudioUrl,
  isHttpUrl,
  extractAudioUrls,
  mergeTranscriptRows,
  getMatchMediaData,
} from 'utils/methods/assignmentTranscripts';
import { Birdimg } from 'utils/assets/images';
import { getQuestionAiPrintData } from 'utils/methods/assignmentPrintAiHelpers';

const PrintRoot = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: #000;
  text-rendering: geometricPrecision;
  -webkit-font-smoothing: subpixel-antialiased;
  -moz-osx-font-smoothing: auto;
  box-sizing: border-box;
  background: white;
  min-height: 100px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;

  @media print {
    @page {
      margin: 1cm;
      size: A4;
    }
    position: static !important;
    left: auto !important;
    top: auto !important;
    visibility: visible !important;
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
    height: auto !important;
    min-height: auto !important;
    background: white !important;
    font-size: 12px !important;
  }
`;

const PrintSectionHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #03989e;
  text-transform: uppercase;
  margin-bottom: 10px;
  padding-bottom: 4px;
  border-bottom: 2px solid #03989e;
`;

const PrintAiBlock = styled.div`
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgb(241, 235, 251);
  font-size: 12px;
  line-height: 1.45;
  page-break-inside: avoid;
  break-inside: avoid;
`;

const PrintAiHeading = styled.div`
  font-weight: 700;
  font-size: 12px;
  color: #1e3a8a;
  margin-bottom: 6px;
`;

const PrintAiGradeBadge = styled.div`
  display: inline-block;
  font-weight: 700;
  font-size: 12px;
  color: #1e3a8a;
  margin-bottom: 8px;
`;

const PrintAiBody = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: #1a1a1a;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PrintSection = styled.div`
  display: block;
  margin-bottom: 18px;
  clear: both;

  @media print {
    display: block !important;
    page-break-after: auto !important;
    orphans: 2 !important;
    widows: 2 !important;
    margin-bottom: 10px !important;
  }
`;

const PrintBreakUnit = styled.div`
  display: block;
  clear: both;
  page-break-inside: avoid;
  break-inside: avoid;

  @media print {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
`;

const PrintQuestionLabel = styled.div`
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
  margin-top: 0;
  font-size: 12px;
  line-height: 1.4;
  clear: both;
  padding-left: 0;
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;

  .print-question-text {
    display: inline !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow-wrap: break-word;
    word-wrap: break-word;
  }

  .print-question-text p,
  .print-question-text div,
  .print-question-text h1,
  .print-question-text h2,
  .print-question-text h3,
  .print-question-text h4,
  .print-question-text h5,
  .print-question-text h6 {
    display: inline !important;
    margin: 0 !important;
    padding: 0 !important;
  }
`;

const PrintAnswerArea = styled(PrintBreakUnit)`
  border: 1px dashed #000;
  border-bottom-width: 2px;
  border-bottom-style: dotted;
  border-bottom-color: #000;
  margin-top: 4px;
  padding: 6px 10px;
  position: relative;
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;

  @media print {
    overflow: visible !important;
  }
`;

const PrintAnswerLabel = styled.div`
  display: block;
  font-weight: 600;
  font-size: 11px;
  margin-bottom: 4px;
  padding-bottom: 2px;
  border-bottom: 1px dashed #ccc;
`;

const PrintAnswer = styled.div`
  display: block;
  margin-top: 0;
  font-size: 12px;
  font-weight: 600;
  clear: both;
  padding-left: 0;
  line-height: 1.4;
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
`;

const PrintAnswerCorrect = styled.span`
  color: #0b5f63;
  display: block !important;
  margin-bottom: 0;
  padding: 0;
  line-height: 1.4;
  font-size: 12px;
`;

const PrintAnswerIncorrect = styled.span`
  color: red;
  display: block !important;
  text-decoration: ${p => p.showStrike ? 'line-through' : 'none'};
  margin-bottom: 0;
  padding: 0;
  line-height: 1.4;
  font-size: 12px;
`;

const PrintImage = styled.img.attrs(() => ({
  crossOrigin: 'anonymous',
  referrerPolicy: 'no-referrer'
}))`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 5px;
  border: 1px solid #e0e0e0;
  margin-top: 4px;
  display: block;
`;

const AudioIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  vertical-align: middle;
  
  svg {
    font-size: 14px;
    color: #666;
  }
`;

const MatchSideContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  max-width: 100%;
`;

const MatchTextContent = styled.span`
  display: inline-block;
  max-width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
`;

const AnswerKeyLabel = styled.div`
  display: block;
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 4px;
  margin-top: 4px;
  color: #000;
`;

const AnswerKeyContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  align-items: center;
  margin-bottom: 6px;
`;

const AnswerKeyItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 1.4;
`;

const MatchBadge = styled.div`
  border-radius: 5px;
  min-width: 20px;
  max-height: 20px;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 5px;
  background-color: #e91e63;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
`;

const MatchAnswerChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 16px;
  margin-left: 8px;
`;

const MatchAnswerChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  position: relative;
  min-width: 150px;
  border-radius: 12px;
  padding: 12px 14px 12px 26px;
  background: linear-gradient(180deg, #2f36ff 0%, #1a25b9 100%);
  color: #fff;
  border: 1px solid #1a1a1a;
`;

const MatchChipClose = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -11px;
  left: -11px;
  z-index: 2;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${p => (p.$isCorrect ? '#0b8f19' : '#e91e63')};
  color: #fff;

  svg {
    font-size: 14px;
    color: #fff;
  }
`;

const MatchChipLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  border-radius: 7px;
  background: #e91e63;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 0 6px;
`;

const MatchAnswerLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 14px;
`;

const MatchLegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #000;
`;

const PrintCollageImage = styled.img`
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  margin: 2px;
  display: block;
`;

const FillingAnswerTokens = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
`;

const FillingAnswerToken = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$isCorrect ? '#4caf50' : '#e57373')};
  background: ${p => (p.$isCorrect ? '#e8f5e9' : '#ffebee')};
  color: #000;
  font-size: 12px;
  line-height: 1.4;
`;

const FillingKeyToken = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  border: 1px solid #1a35ff;
  background: #dbe7f5;
  color: #2f3747;
  font-size: 12px;
  line-height: 1.4;
  margin-right: 6px;
  margin-bottom: 4px;
`;

const getMatchBadgeLabel = (index) => {
  if (!Number.isInteger(index) || index < 0) return '-';
  return String.fromCharCode(65 + index);
};

function stripHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}

function formatFillingAnswer(selectedAnswer) {
  if (!selectedAnswer || !Array.isArray(selectedAnswer)) return '';
  return selectedAnswer.map((item) => get(item, 'text', get(item, 'answer', ''))).filter(Boolean).join(', ');
}

function stripLeadingNumbers(html) {
  if (!html) return html;
  if (typeof html !== 'string') return html;
  return html.replace(/^(\s*)(\d+[\.\)]\s*)/i, '$1').trim();
}

function normalizeFillingOptionValues(optionValues) {
  if (!Array.isArray(optionValues)) return [];
  return optionValues
    .map((value) => {
      if (typeof value === 'string') return value.trim().toLowerCase();
      if (value && typeof value === 'object') {
        return String(value.text || value.answer || value.value || '').trim().toLowerCase();
      }
      return '';
    })
    .filter(Boolean);
}

function normalizeFillingSelections(selection, correctOptionValues = []) {
  const selectedItems = Array.isArray(selection) ? selection : (selection ? [selection] : []);
  const normalizedCorrect = normalizeFillingOptionValues(correctOptionValues);

  return selectedItems
    .map((item) => {
      const label = typeof item === 'string'
        ? item
        : String(get(item, 'text', get(item, 'answer', get(item, 'value', ''))));
      if (!label) return null;

      const hasExplicitCorrectness = typeof get(item, 'is_correct') === 'boolean';
      const isCorrect = hasExplicitCorrectness
        ? Boolean(get(item, 'is_correct'))
        : normalizedCorrect.includes(label.trim().toLowerCase());

      return {
        label,
        isCorrect
      };
    })
    .filter(Boolean);
}

function stripFillingAnswerLabel(text) {
  if (!text || typeof text !== 'string') return '';
  return text.replace(/^\s*-?\s*Answer\s+\d+\s+of\s+\d+\s*-*\s*/i, '').trim();
}

function buildFillingPreviewText(option, highlights = [], selection = []) {
  const cleanOption = stripFillingAnswerLabel(stripHtml(option || ''));
  const selectedItems = Array.isArray(selection) ? selection : (selection ? [selection] : []);
  if (!cleanOption) return '';
  if (!Array.isArray(highlights) || highlights.length === 0) return cleanOption;

  const sortedHighlights = highlights
    .filter((item) => item && Number.isFinite(item.start) && Number.isFinite(item.end))
    .sort((a, b) => a.start - b.start);

  if (sortedHighlights.length === 0) return cleanOption;

  let currentIndex = 0;
  let mergedText = '';

  sortedHighlights.forEach((highlight, index) => {
    const safeStart = Math.max(0, highlight.start);
    const safeEnd = Math.max(safeStart, highlight.end);
    if (safeStart > currentIndex) {
      mergedText += cleanOption.slice(currentIndex, safeStart);
    }
    const selected = selectedItems[index];
    const selectedLabel = typeof selected === 'string'
      ? selected
      : String(get(selected, 'answer', get(selected, 'text', get(selected, 'value', ''))));
    mergedText += selectedLabel || cleanOption.slice(safeStart, safeEnd);
    currentIndex = safeEnd;
  });

  if (currentIndex < cleanOption.length) {
    mergedText += cleanOption.slice(currentIndex);
  }

  return mergedText.replace(/\s+/g, ' ').trim();
}

function buildFillingInlineSegments(option, highlights = [], selectedTokens = []) {
  const cleanOption = stripFillingAnswerLabel(stripHtml(option || ''));
  if (!cleanOption) return [];
  if (!Array.isArray(highlights) || highlights.length === 0) {
    return [{ type: 'text', value: cleanOption }];
  }

  const sortedHighlights = highlights
    .filter((item) => item && Number.isFinite(item.start) && Number.isFinite(item.end))
    .sort((a, b) => a.start - b.start);

  if (sortedHighlights.length === 0) {
    return [{ type: 'text', value: cleanOption }];
  }

  const segments = [];
  let currentIndex = 0;

  sortedHighlights.forEach((highlight, index) => {
    const safeStart = Math.max(0, highlight.start);
    const safeEnd = Math.max(safeStart, highlight.end);
    if (safeStart > currentIndex) {
      segments.push({ type: 'text', value: cleanOption.slice(currentIndex, safeStart) });
    }

    const token = selectedTokens[index];
    const tokenLabel = typeof token?.label === 'string' ? token.label : '';
    if (tokenLabel.trim()) {
      segments.push({ type: 'token', value: tokenLabel, isCorrect: Boolean(token?.isCorrect) });
    }
    currentIndex = safeEnd;
  });

  if (currentIndex < cleanOption.length) {
    segments.push({ type: 'text', value: cleanOption.slice(currentIndex) });
  }

  return segments;
}

function renderMatchMediaValue(data, keyPrefix, options = {}) {
  const { hideImage = false } = options;
  if (!data) {
    return <MatchTextContent>-</MatchTextContent>;
  }

  if (data.type === 'image') {
    if (hideImage) {
      return ;
    }
    return data.url ? <PrintImage src={data.url} alt={`${keyPrefix}-image`} /> : <MatchTextContent>-</MatchTextContent>;
  }

  if (data.type === 'audio') {
    return (
      <MatchSideContent>
        <Mic style={{ fontSize: '16px', color: '#666' }} />
        <MatchTextContent>{data.transcript || data.text || '[Audio Response]'}</MatchTextContent>
      </MatchSideContent>
    );
  }

  if (data.type === 'imageAudio') {
    if (hideImage) {
      return (
        <MatchSideContent>
          <Mic style={{ fontSize: '16px', color: '#666' }} />
          <MatchTextContent>{data.transcript || data.text || '[Audio Response]'}</MatchTextContent>
        </MatchSideContent>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        {data.url ? <PrintImage src={data.url} alt={`${keyPrefix}-image-audio`} /> : null}
        <MatchSideContent>
          <Mic style={{ fontSize: '16px', color: '#666' }} />
          <MatchTextContent>{data.transcript || data.text || '[Audio Response]'}</MatchTextContent>
        </MatchSideContent>
      </div>
    );
  }

  return <MatchTextContent>{data.text || '-'}</MatchTextContent>;
}

function getAnswerData(question, answer, index, transcripts = {}, allQuestions = []) {
  if (!question) {
    return null;
  }

  const questionsList = Array.isArray(allQuestions) ? allQuestions : [];

  const ans = get(answer, 'answer');
  const qAnswer = get(question, 'answer');
  const options = get(question, 'options', []);
  const isCorrect = get(answer, 'is_correct', false);
  switch (question.type) {
    case 'multipleChoice': {
      const studentAnswerText = answer?.isStudentHaveDiff && answer?.studentDiffOption
        ? answer.studentDiffOption[ans]?.value
        : options[ans];
      const correctAnswerText = answer?.isStudentHaveDiff && answer?.studentDiffOption
        ? answer.studentDiffOption[Number(qAnswer)]?.value || options[Number(qAnswer)]
        : options[Number(qAnswer)];
      const mcIsCorrect = answer?.isStudentHaveDiff 
        ? answer.correct_answers == answer.answer 
        : qAnswer == ans;
      
      return {
        studentAnswer: studentAnswerText,
        correctAnswer: correctAnswerText,
        isCorrect: mcIsCorrect,
        showCorrect: !mcIsCorrect
      };
    }
    case 'trueFalse':
    case 'audioTrueFalse':
    case 'audioMultipleChoice': {
      const studentAnswerText = options[ans];
      const correctAnswerText = options[qAnswer];
      const tfIsCorrect = qAnswer == ans;
      
      return {
        studentAnswer: studentAnswerText,
        correctAnswer: correctAnswerText,
        isCorrect: tfIsCorrect,
        showCorrect: !tfIsCorrect
      };
    }
    case 'multipleResponse': {
      if (!Array.isArray(ans)) {
        return {
          studentAnswer: '',
          correctAnswer: Array.isArray(qAnswer)
            ? qAnswer.map((idx) => options[idx]).filter(Boolean).join(', ')
            : [options[qAnswer]].filter(Boolean).join(', '),
          isCorrect: false,
          showCorrect: true
        };
      }
      const studentAnswers = ans.map((idx) => options[idx]).filter(Boolean);
      const correctAnswers = Array.isArray(qAnswer) 
        ? qAnswer.map((idx) => options[idx]).filter(Boolean)
        : [options[qAnswer]].filter(Boolean);
      const mrIsCorrect = isCorrect || (Array.isArray(qAnswer) && ans.length === qAnswer.length && ans.every(a => qAnswer.includes(a)));
      
      return {
        studentAnswer: studentAnswers.join(', '),
        correctAnswer: correctAnswers.join(', '),
        isCorrect: mrIsCorrect,
        showCorrect: !mrIsCorrect
      };
    }
    case 'essay':
    case 'audioEssay':
    case 'email': {
      const essayAnswer = typeof ans === 'string' ? ans : null;
      return {
        studentAnswer: essayAnswer,
        isCorrect: true,
        showCorrect: false
      };
    }
    case 'audio':
    case 'twoMinAudio': {
      const hasAudioResponse = typeof ans === 'string' ? Boolean(ans.trim()) : Boolean(ans);
      const audioUrl = hasAudioResponse && typeof ans === 'string' && ans.startsWith('http') ? ans : null;
      const transcript = audioUrl ? lookupTranscript(transcripts, audioUrl) : null;
      return {
        studentAnswer: transcript || (hasAudioResponse ? '[Audio response]' : '-'),
        isCorrect: true,
        showCorrect: false,
        audioUrl: audioUrl,
        transcript: transcript,
        hasAudioResponse: hasAudioResponse
      };
    }
    case 'video': {
      const hasVideoResponse = typeof ans === 'string' ? Boolean(ans.trim()) : Boolean(ans);
      const answerVideoUrl =
        hasVideoResponse && typeof ans === 'string' && isHttpUrl(ans.trim()) ? ans.trim() : null;
      const questionVideoUrl =
        get(question, 'video') && typeof get(question, 'video') === 'string' && isHttpUrl(get(question, 'video').trim())
          ? get(question, 'video').trim()
          : null;
      const answerTranscript = answerVideoUrl ? lookupTranscript(transcripts, answerVideoUrl) : null;
      const questionTranscript = questionVideoUrl ? lookupTranscript(transcripts, questionVideoUrl) : null;
      return {
        studentAnswer: answerTranscript || (hasVideoResponse ? '[Video response]' : '-'),
        hasVideoResponse,
        isCorrect: true,
        showCorrect: false,
        videoUrl: answerVideoUrl,
        transcript: answerTranscript,
        questionVideoUrl,
        questionTranscript,
      };
    }
    case 'filling':
    case 'fillingImage': {
      const optionCount = get(question, 'options.length', 0);
      if (optionCount === 0) {
        return null;
      }
      const isFillingAudioType = get(question, 'fillingType') === 'Audio';
      const fillingParts = map(question.options, (option, optId) => {
        const sel = get(answer, 'selected_answer') && answer.selected_answer[optId];
        const filled = formatFillingAnswer(Array.isArray(sel) ? sel : (sel ? [sel] : []));
        const optionHighlights = get(question, `highlightWords[${optId}]`, []);
        const optionText = buildFillingPreviewText(option, optionHighlights, sel);
        const mediaUrl = resolveMediaUrl(get(question, `filling_image[${optId}]`, ''));
        const correctOptionValues = get(question, `answerSelected[${optId}]`, []);
        const selectedTokens = normalizeFillingSelections(sel, correctOptionValues);
        return {
          partNumber: optId + 1,
          optionText: optionText || '',
          filled,
          selectedTokens,
          inlineSegments: buildFillingInlineSegments(option, optionHighlights, selectedTokens),
          imageUrl: isFillingAudioType ? '' : (mediaUrl || ''),
          audioUrl: isFillingAudioType ? (mediaUrl || '') : '',
          audioTranscript: isFillingAudioType && mediaUrl ? lookupTranscript(transcripts, mediaUrl) : null
        };
      });
      const fillingAnswer = fillingParts
        .map((part) => part.optionText ? `Part ${part.partNumber}: ${part.optionText}${part.filled ? ` [${part.filled}]` : ''}` : null)
        .filter(Boolean)
        .join('\n');
      return {
        studentAnswer: fillingAnswer,
        fillingParts: fillingParts,
        isCorrect: true,
        showCorrect: false
      };
    }
    case 'picture': {
      const categoryType = get(question, 'categoryType', '');
      const answerValue = get(answer, 'answer');
      const imageUrl = get(answer, 'imageUrl') || (categoryType === 'Image' || categoryType === 'ImageAndText' ? answerValue : '');
      const additionalImages = get(answer, 'additionalImages', []);
      const allImages = [];
      if (imageUrl) {
        allImages.push(imageUrl);
      }
      if (Array.isArray(additionalImages) && additionalImages.length > 0) {
        allImages.push(...additionalImages);
      }
      const answerText = categoryType === 'Text' || categoryType === 'ImageAndText'
        ? (typeof answerValue === 'string' ? answerValue : '')
        : '';
      const answerAudio = categoryType === 'Audio' && typeof answerValue === 'string' ? answerValue : '';
      const audioTranscript = answerAudio ? lookupTranscript(transcripts, answerAudio) : null;
      return {
        studentAnswer: allImages.length > 0 ? '[Image response]' : (answerText || (answerAudio ? '[Audio response]' : null)),
        isCorrect: true,
        showCorrect: false,
        categoryType,
        imageUrl: imageUrl,
        additionalImages: additionalImages,
        allImages: allImages,
        answerText,
        answerAudio,
        audioTranscript
      };
    }
    case 'match':
    case 'matchMaking': {
      const matchData = get(answer, 'match_data', []);
      const questionOptions = get(question, 'options', []);
      const questionType = get(question, 'questionType', 'Text');
      const matchType = get(question, 'matchType', 'Image');
      const normalizedMatchData = (Array.isArray(matchData) ? matchData : []).filter(
        (entry) => Number.isInteger(entry?.item) && Number.isInteger(entry?.match_item)
      );
      const isAllCorrect =
        normalizedMatchData.length > 0 &&
        normalizedMatchData.length === questionOptions.length &&
        normalizedMatchData.every((entry) => Boolean(entry?.is_correct));

      const formattedMatches = normalizedMatchData.map((match) => {
        const optionIndex = match.item;
        const leftOption = questionOptions[optionIndex];
        const rightOption = Number.isInteger(match?.match_item) ? questionOptions[match.match_item] : null;
        const leftMedia = getMatchMediaData(leftOption, 'left', questionType, matchType, transcripts);
        const rightMedia = getMatchMediaData(rightOption, 'right', questionType, matchType, transcripts);

        return {
          left: leftMedia?.text || `Item ${optionIndex + 1}`,
          right: rightMedia?.text || '',
          leftData: leftMedia,
          rightData: rightMedia,
          leftIndex: optionIndex,
          matchedIndex: Number.isInteger(match?.match_item) ? match.match_item : null,
          isCorrect: Boolean(match?.is_correct)
        };
      }).filter((m) => m.left);

      const answerKeyPairs = map(questionOptions, (option, optionIndex) => {
        const rightMedia = getMatchMediaData(option, 'right', questionType, matchType, transcripts);
        return {
          badge: getMatchBadgeLabel(optionIndex),
          value: rightMedia?.text || `Match ${optionIndex + 1}`,
          valueData: rightMedia
        };
      }).filter((pair) => String(pair.value || '').trim());
      
      return {
        studentAnswer: formattedMatches.length > 0 ? formattedMatches.map(m => `${m.left} → ${m.right || '-'}`).join(', ') : '[No matches]',
        isCorrect: isAllCorrect,
        showCorrect: false,
        matchData: formattedMatches,
        answerKeyPairs
      };
    }
    case 'photoCollage': {
      const collageFrames = get(answer, 'answer', []);
      const answerText = get(answer, 'answer_text', '');
      const answerAudio = get(answer, 'answer_audio', '');
      const categoryType = get(question, 'categoryType', []);
      const collageImages = Array.isArray(collageFrames) 
        ? collageFrames.map(frame => frame?.image).filter(Boolean)
        : [];
      const audioTranscript = answerAudio ? lookupTranscript(transcripts, answerAudio) : null;

      return {
        studentAnswer: collageImages.length > 0 ? '[Photo Collage]' : null,
        isCorrect: true,
        showCorrect: false,
        collageImages: collageImages,
        answerText: answerText,
        answerAudio: answerAudio,
        audioTranscript: audioTranscript,
        hasText: Array.isArray(categoryType) && categoryType.includes('Text'),
        hasAudio: Array.isArray(categoryType) && categoryType.includes('Audio')
      };
    }
    case 'audioToImage': {
      const imageUrl = get(answer, 'imageUrl') || get(answer, 'answer');
      const additionalImages = get(answer, 'additionalImages', []);
      const allImages = [];
      if (imageUrl) {
        if (typeof imageUrl === 'string') {
          allImages.push(imageUrl);
        } else if (Array.isArray(imageUrl)) {
          allImages.push(...imageUrl.filter(Boolean));
        }
      }
      if (Array.isArray(additionalImages) && additionalImages.length > 0) {
        allImages.push(...additionalImages.filter(Boolean));
      }
      const questionAudioUrl = resolveGroupStemAudioUrl(question, questionsList) || get(question, 'audio');
      const questionTranscript = questionAudioUrl ? lookupTranscript(transcripts, questionAudioUrl) : null;

      return {
        studentAnswer: allImages.length > 0 ? '[Image response]' : null,
        isCorrect: true,
        showCorrect: false,
        imageUrl: imageUrl,
        additionalImages: additionalImages,
        allImages: allImages,
        questionAudioUrl: questionAudioUrl,
        questionTranscript: questionTranscript
      };
    }
    default: {
      const defaultAnswer = typeof ans === 'string' ? ans : (Array.isArray(ans) ? ans.join(', ') : null);
      return {
        studentAnswer: defaultAnswer,
        isCorrect: true,
        showCorrect: false
      };
    }
  }
}

function getQuestionText(question, index) {
  if (!question) return null;
  const q = get(question, 'question', '');
  if (typeof q !== 'string') return '';
  return stripLeadingNumbers(q);
}

const PRINTABLE_TYPES = [
  'multipleChoice',
  'trueFalse',
  'multipleResponse',
  'filling',
  'fillingImage',
  'essay',
  'audioTrueFalse',
  'audioMultipleChoice',
  'audioEssay',
  'email',
  'audio',
  'twoMinAudio',
  'video',
  'picture',
  'match',
  'matchMaking',
  'photoCollage',
  'audioToImage',
];

const BANNER_META_ROWS = [
  { key: 'student', label: 'Student:' },
  { key: 'class', label: 'Class:' },
  { key: 'activity', label: 'Activity:' },
  { key: 'overallGrade', label: 'Grade:' },
  { key: 'proficiencyLevel', label: 'Level for AI grading:' },
];

const renderBannerMetaGrid = ({
  studentName,
  classDisplayName,
  activityTitle,
  overallGrade,
  proficiencyLevel,
}) => {
  const values = {
    student: studentName,
    class: classDisplayName,
    activity: activityTitle,
    overallGrade,
    proficiencyLevel,
  };
  const rows = BANNER_META_ROWS.filter(({ key }) => values[key]);
  if (rows.length === 0) {
    return null;
  }
  return (
    <div
      className="assignment-print-banner-meta-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        columnGap: '10px',
        rowGap: '4px',
        width: '100%',
        fontSize: '12px',
        lineHeight: '16px',
        color: '#ffffff',
        alignContent: 'center',
      }}
    >
      {rows.map(({ key, label }) => (
        <React.Fragment key={key}>
          <span
            className="assignment-print-banner-meta-label"
            style={{
              fontWeight: 700,
              textAlign: 'right',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </span>
          <span
            className="assignment-print-banner-meta-value"
            style={{
              fontWeight: 500,
              textAlign: 'left',
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
            }}
          >
            {values[key]}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

const renderPrintAiForQuestion = (questionIndex, aiGrades, aiFeedbacks, aiSuggestions) => {
  const aiData = getQuestionAiPrintData(questionIndex, aiGrades, aiFeedbacks, aiSuggestions);
  if (!aiData) {
    return null;
  }
  const { aiGrade, isRubrics, showGradeScore, explanationHtml, errorsHtml, suggestionText } = aiData;

  return (
    <PrintAiBlock className="print-ai-block print-break-unit">
      {isRubrics ? (
        <PrintAiGradeBadge>Calificado por IA: Rúbricas</PrintAiGradeBadge>
      ) : showGradeScore ? (
        <PrintAiGradeBadge>
          Calificado por IA: {aiGrade.ai_grade_point ?? '-'} / {aiGrade.max_mark_per_question ?? '-'}
        </PrintAiGradeBadge>
      ) : null}
      {explanationHtml ? (
        <div>
          <PrintAiHeading>Explicación del grado de IA</PrintAiHeading>
          <PrintAiBody>
            <MessageRenderer data={explanationHtml} />
          </PrintAiBody>
        </div>
      ) : null}
      {errorsHtml ? (
        <div>
          <PrintAiHeading>Errores y cambios necesarios</PrintAiHeading>
          <PrintAiBody>
            <MessageRenderer data={errorsHtml} />
          </PrintAiBody>
        </div>
      ) : null}
      {suggestionText ? (
        <div>
          <PrintAiHeading>Consejos para mejorar</PrintAiHeading>
          <PrintAiBody>
            <MessageRenderer data={suggestionText} />
          </PrintAiBody>
        </div>
      ) : null}
    </PrintAiBlock>
  );
};

const renderPrintPageStyleHeader = ({
  studentName,
  classDisplayName,
  activityTitle,
  overallGrade,
  proficiencyLevel,
}) => (
  <div className="assignment-print-page-header" style={{ width: '100%' }}>
    <div
      className="assignment-print-banner"
      style={{
        display: 'block',
        width: '100%',
        backgroundColor: '#2d8bba',
        color: '#ffffff',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    >
      <div
        className="assignment-print-banner-inner"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 20px 12px 40px',
          minHeight: '100px',
          boxSizing: 'border-box',
          gap: '16px',
        }}
      >
        <div
          className="assignment-print-banner-brand"
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            flex: '0 0 50%',
            maxWidth: '50%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          <div style={{ position: 'absolute', left: -80, top: -30 }}>
            <img
              className="assignment-print-banner-bird"
              src={Birdimg}
              alt=""
              crossOrigin="anonymous"
              style={{ height: '100px', width: 'auto', display: 'block' }}
            />
          </div>
          <p
            className="assignment-print-banner-title"
            style={{ margin: 0, fontSize: '20px', lineHeight: '30px', color: '#ffffff' }}
          >
            One Way Education
          </p>
        </div>
        <div
          className="assignment-print-banner-meta-column"
          style={{
            flex: '0 0 50%',
            maxWidth: '50%',
            minWidth: 0,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingLeft: '8px',
          }}
        >
          {renderBannerMetaGrid({
            studentName,
            classDisplayName,
            activityTitle,
            overallGrade,
            proficiencyLevel,
          })}
        </div>
      </div>
    </div>
  </div>
);

const AssignmentPrintView = React.forwardRef(({
  questions = [],
  answers = [],
  studentName = '',
  classDisplayName = '',
  activityTitle = '',
  overallGrade = '',
  proficiencyLevel = '',
  aiGrades = [],
  aiFeedbacks = [],
  aiSuggestions = [],
}, ref) => {
  const list = Array.isArray(questions) ? questions : [];
  const answersList = Array.isArray(answers) ? answers : [];
  const [transcripts, setTranscripts] = useState({});
  const [transcriptsLoading, setTranscriptsLoading] = useState(false);
  const containerRef = React.useRef(null);

  const fetchTranscripts = async (audioUrls) => {
    if (!audioUrls || audioUrls.length === 0) {
      setTranscriptsLoading(false);
      if (containerRef.current) {
        containerRef.current.setAttribute('data-transcripts-loaded', 'true');
      }
      return;
    }

    setTranscriptsLoading(true);
    if (containerRef.current) {
      containerRef.current.removeAttribute('data-transcripts-loaded');
    }

    try {
      const response = await handleGetTranscripts(audioUrls);

      if (response && response.transcripts) {
        const transcriptMap = mergeTranscriptRows(response.transcripts, audioUrls);
        setTranscripts((prev) => ({ ...prev, ...transcriptMap }));
      }

      const missingUrls = Array.isArray(response?.missing_urls)
        ? response.missing_urls.filter((url) => typeof url === 'string' && url.trim())
        : [];
      if (missingUrls.length > 0) {
        await Promise.all(
          missingUrls.map((url) =>
            handleAudioTranscript(url.trim()).catch((err) => {
              console.error('Error queueing transcript for missing URL:', err);
            })
          )
        );
        const retryResponse = await handleGetTranscripts(audioUrls);
        if (retryResponse?.transcripts) {
          const retryMap = mergeTranscriptRows(retryResponse.transcripts, audioUrls);
          setTranscripts((prev) => ({ ...prev, ...retryMap }));
        }
      }
    } catch (error) {
      console.error('Error fetching transcripts:', error);
    } finally {
      setTranscriptsLoading(false);
      if (containerRef.current) {
        containerRef.current.setAttribute('data-transcripts-loaded', 'true');
      }
    }
  };

  useEffect(() => {
    const audioUrls = extractAudioUrls(list, answersList);
    if (audioUrls.length > 0) {
      fetchTranscripts(audioUrls);
    } else {
      setTranscriptsLoading(false);
      if (containerRef.current) {
        containerRef.current.setAttribute('data-transcripts-loaded', 'true');
      }
    }
  }, [list, answersList]);

  const printableItems = list
    .map((question, index) => {
      if (!PRINTABLE_TYPES.includes(question.type)) return null;
      const answer = answersList[index];
      const answerData = getAnswerData(question, answer, index, transcripts, list);
      if (!answerData) return null;
      return { question, answer, answerData, index };
    })
    .filter(Boolean);
  if (printableItems.length === 0) {
    return (
      <PrintRoot className="assignment-print-area" id="assignment-print-area">
        <div ref={ref} className="print-container printable">
          {renderPrintPageStyleHeader({
            studentName,
            classDisplayName,
            activityTitle,
            overallGrade,
            proficiencyLevel,
          })}
          <PrintSectionHeader className="print-break-unit">QUESTIONS & ANSWERS</PrintSectionHeader>
          <div>No questions to print</div>
        </div>
      </PrintRoot>
    );
  }

  return (
    <PrintRoot className="assignment-print-area" id="assignment-print-area">
      <div ref={(node) => {
        containerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }} className="print-container printable" style={{ width: '100%' }}>
        {renderPrintPageStyleHeader({
          studentName,
          classDisplayName,
          activityTitle,
          overallGrade,
          proficiencyLevel,
        })}
        <PrintSectionHeader className="print-break-unit">QUESTIONS & ANSWERS</PrintSectionHeader>
        {printableItems.map((item, displayIndex) => {
          const { question, answerData } = item;
          const questionText = getQuestionText(question, item.index);
          const isAudioType = ['audio', 'twoMinAudio', 'audioTrueFalse', 'audioMultipleChoice', 'audioEssay', 'audioToImage'].includes(question.type);
          const isFillingImageType =
            question.type === 'fillingImage' || get(question, 'fillingType') === 'Image';
          const isFillingAudioType =
            (question.type === 'filling' || question.type === 'fillingImage') && get(question, 'fillingType') === 'Audio';
          const hasPicture = get(question, 'pictures');
          const hasImage = get(question, 'image');
          const isVideo = question.type === 'video';
          const questionVideoTranscript = isVideo ? answerData?.questionTranscript : null;
          const isPictureType = question.type === 'picture';
          const questionImages = isPictureType
            ? [
                get(question, 'pictures'),
                ...((get(question, 'additionalPictures', []) || [])
                  .map((img) => (typeof img === 'string' ? img : img?.url))
                  .filter(Boolean))
              ].filter(Boolean)
            : [];
          const number = displayIndex + 1;
          const questionAudioUrl = isAudioType ? resolveGroupStemAudioUrl(question, list) || get(question, 'audio') : null;
          const questionTranscript = questionAudioUrl ? lookupTranscript(transcripts, questionAudioUrl) : null;
          const isFillingType = question.type === 'filling' || question.type === 'fillingImage';
          const questionFillingParts = isFillingType
            ? map(get(question, 'options', []), (option, optId) => {
                const mediaUrl = resolveMediaUrl(get(question, `filling_image[${optId}]`, ''));
                const correctOptionValues = get(question, `answerSelected[${optId}]`, []);
                const keyTokens = normalizeFillingSelections(correctOptionValues, correctOptionValues);
                return {
                  partNumber: optId + 1,
                  imageUrl: isFillingAudioType ? '' : (mediaUrl || ''),
                  audioUrl: isFillingAudioType ? (mediaUrl || '') : '',
                  audioTranscript: isFillingAudioType && mediaUrl ? lookupTranscript(transcripts, mediaUrl) : null,
                  keyTokens
                };
              })
            : [];
          const printableQuestionText = questionText;

          return (
            <PrintSection key={`print-q-${displayIndex}`} className="print-section-item">
              <PrintBreakUnit className="print-break-unit">
              <PrintQuestionLabel>
                {number}. <span className="print-question-text" style={{ display: 'inline' }} dangerouslySetInnerHTML={{ __html: printableQuestionText || '' }} />
              </PrintQuestionLabel>
              {isVideo && get(question, 'video') && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '4px', marginBottom: '4px', paddingLeft: '0' }}>
                  <AudioIconWrapper style={{ marginLeft: 0, flexShrink: 0 }}>
                    <Videocam />
                  </AudioIconWrapper>
                  {questionVideoTranscript && (
                    <div style={{ flex: 1, fontSize: '12px', lineHeight: '1.4', fontWeight: 400 }}>
                      <MessageRenderer data={questionVideoTranscript} />
                    </div>
                  )}
                </div>
              )}
              {isAudioType && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '4px', marginBottom: '4px', paddingLeft: '0' }}>
                  <AudioIconWrapper style={{ marginLeft: 0, flexShrink: 0 }}>
                    <Mic />
                  </AudioIconWrapper>
                  {questionTranscript && (
                    <div style={{ flex: 1, fontSize: '12px', lineHeight: '1.4', fontWeight: 400 }}>
                      <MessageRenderer data={questionTranscript} />
                    </div>
                  )}
                </div>
              )}
              {!isPictureType && hasPicture && <PrintImage src={question.pictures} alt="" />}
              {!isPictureType && hasImage && <PrintImage src={question.image} alt="" />}
              {isPictureType && questionImages.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                  {questionImages.map((imgUrl, idx) => (
                    <PrintImage key={idx} src={imgUrl} alt={`Question image ${idx + 1}`} />
                  ))}
                </div>
              )}
              {(question.type === 'match' || question.type === 'matchMaking') && Array.isArray(answerData.matchData) && answerData.matchData.length > 0 && (
                <>
                  <AnswerKeyLabel>Answer Key:</AnswerKeyLabel>
                  <AnswerKeyContainer>
                    {map(get(question, 'options', []), (option, optId) => {
                      const keyData = getMatchMediaData(option, 'left', get(question, 'questionType', 'Text'), get(question, 'matchType', 'Image'), transcripts);
                      return (
                        <AnswerKeyItem key={optId}>
                          {renderMatchMediaValue(keyData, `key-${optId}`, { hideImage: true })}
                          <MatchBadge>
                            {getMatchBadgeLabel(optId)}
                          </MatchBadge>
                        </AnswerKeyItem>
                      );
                    })}
                  </AnswerKeyContainer>
                </>
              )}
              {isFillingType && questionFillingParts.length > 0 && (
                <div style={{ marginTop: '8px', marginBottom: '4px' }}>
                  {questionFillingParts.map((part, partIdx) => (
                    <div key={`question-filling-part-${partIdx}`} style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                        {`Answer Key ${part.partNumber} of ${questionFillingParts.length}`}
                      </div>
                      {part.imageUrl ? <PrintImage src={part.imageUrl} alt={`Answer key part ${part.partNumber}`} /> : null}
                      {part.audioUrl ? (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '4px' }}>
                          <Mic style={{ fontSize: '16px', color: '#666', marginTop: '1px' }} />
                          <div style={{ flex: 1, fontSize: '12px', lineHeight: '1.4' }}>
                            {part.audioTranscript ? (
                              <MessageRenderer data={part.audioTranscript} />
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                      {Array.isArray(part.keyTokens) && part.keyTokens.length > 0 ? (
                        <div style={{ marginTop: '6px' }}>
                          {part.keyTokens.map((token, tokenIdx) => (
                            <FillingKeyToken key={`question-key-token-${partIdx}-${tokenIdx}`}>
                              {token.label}
                            </FillingKeyToken>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
              </PrintBreakUnit>
              <PrintAnswerArea className="print-break-unit print-answer-area">
                <PrintAnswerLabel>Answer</PrintAnswerLabel>
                {question.type === 'picture' ? (
                  <>
                    {answerData.allImages && answerData.allImages.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                        {answerData.allImages.map((imgUrl, idx) => (
                          <PrintImage key={idx} src={imgUrl} alt={`Answer ${idx + 1}`} />
                        ))}
                      </div>
                    )}
                    {answerData.answerText && (
                      <div style={{ marginTop: '6px', fontSize: '12px', lineHeight: '1.4' }}>
                        <MessageRenderer data={answerData.answerText} />
                      </div>
                    )}
                    {answerData.answerAudio && (
                      <div style={{ marginTop: '6px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                        <AudioIconWrapper style={{ marginLeft: 0, flexShrink: 0 }}>
                          <Mic />
                        </AudioIconWrapper>
                        <div style={{ flex: 1, lineHeight: '1.4' }}>
                          {answerData.audioTranscript ? (
                            <MessageRenderer data={answerData.audioTranscript} />
                          ) : (
                            <span>[Audio response]</span>
                          )}
                        </div>
                      </div>
                    )}
                    {!answerData.allImages?.length && !answerData.answerText && !answerData.answerAudio && (
                      <PrintAnswerCorrect>-</PrintAnswerCorrect>
                    )}
                  </>
                ) : question.type === 'audioToImage' ? (
                  <>
                    {answerData.allImages && answerData.allImages.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                        {answerData.allImages.map((imgUrl, idx) => (
                          <PrintImage key={idx} src={imgUrl} alt={`Answer ${idx + 1}`} />
                        ))}
                      </div>
                    ) : (
                      <PrintAnswerCorrect>-</PrintAnswerCorrect>
                    )}
                  </>
                ) : (question.type === 'match' || question.type === 'matchMaking') && answerData.matchData ? (
                  <div style={{ marginTop: '4px' }}>
                    {answerData.matchData.length > 0 ? (
                      <>
                        <MatchAnswerChips>
                          {answerData.matchData.map((match, idx) => (
                            <MatchAnswerChip key={idx}>
                              <MatchChipClose $isCorrect={match.isCorrect}>
                                {match.isCorrect ? <Check /> : <Close />}
                              </MatchChipClose>
                              {Number.isInteger(match.matchedIndex) && (
                                <MatchChipLabel>{getMatchBadgeLabel(match.matchedIndex)}</MatchChipLabel>
                              )}
                              {renderMatchMediaValue(match.leftData, `match-left-${idx}`)}
                            </MatchAnswerChip>
                          ))}
                        </MatchAnswerChips>
                        {Array.isArray(answerData.answerKeyPairs) && answerData.answerKeyPairs.length > 0 && (
                          <MatchAnswerLegend>
                            {answerData.answerKeyPairs.map((pair, pairIndex) => (
                              <MatchLegendItem key={`match-key-${pairIndex}`}>
                                <MatchChipLabel>{pair.badge}</MatchChipLabel>
                                {renderMatchMediaValue(pair.valueData, `match-key-${pairIndex}`)}
                              </MatchLegendItem>
                            ))}
                          </MatchAnswerLegend>
                        )}
                      </>
                    ) : (
                      <PrintAnswerCorrect>-</PrintAnswerCorrect>
                    )}
                  </div>
                ) : question.type === 'photoCollage' && (answerData.collageImages?.length > 0 || answerData.hasText || answerData.hasAudio) ? (
                  <>
                    {answerData.collageImages && answerData.collageImages.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                        {answerData.collageImages.map((imgUrl, idx) => (
                          <PrintCollageImage key={idx} src={imgUrl} alt={`Collage ${idx + 1}`} />
                        ))}
                      </div>
                    )}
                    {answerData.hasText && answerData.answerText && (
                      <div style={{ marginTop: '4px', fontSize: '12px', lineHeight: '1.4' }}>
                        <MessageRenderer data={answerData.answerText} />
                      </div>
                    )}
                    {answerData.hasAudio && answerData.answerAudio && (
                      <div style={{ marginTop: '4px', fontSize: '12px' }}>
                        {answerData.audioTranscript ? (
                          <div style={{ lineHeight: '1.4' }}>
                            <MessageRenderer data={answerData.audioTranscript} />
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <VolumeUp style={{ fontSize: '18px', color: '#666' }} />
                            <span style={{ fontWeight: 500 }}>Audio Response</span>
                          </div>
                        )}
                      </div>
                    )}
                    {!answerData.collageImages?.length &&
                      !(answerData.hasText && answerData.answerText) &&
                      !(answerData.hasAudio && answerData.answerAudio) && (
                        <PrintAnswerCorrect>-</PrintAnswerCorrect>
                      )}
                  </>
                ) : (question.type === 'filling' || question.type === 'fillingImage') && Array.isArray(answerData.fillingParts) && answerData.fillingParts.length > 0 ? (
                  <div style={{ marginTop: '4px' }}>
                    {answerData.fillingParts.map((part, partIdx) => (
                      <div key={`filling-part-${partIdx}`} style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                          {`Answer ${part.partNumber} of ${answerData.fillingParts.length}`}
                        </div>
                        <div style={{ marginTop: '6px' }}>
                          {Array.isArray(part.inlineSegments) &&
                          part.inlineSegments.length > 0 &&
                          part.inlineSegments.some(
                            (segment) => segment.type === 'token' && String(segment.value || '').trim()
                          ) ? (
                            <div style={{ fontSize: '12px', lineHeight: '1.9' }}>
                              {part.inlineSegments.map((segment, segmentIdx) => (
                                segment.type === 'token' ? (
                                  <FillingAnswerToken key={`token-${partIdx}-${segmentIdx}`} $isCorrect={segment.isCorrect}>
                                    {segment.value}
                                  </FillingAnswerToken>
                                ) : (
                                  <span key={`text-${partIdx}-${segmentIdx}`}>{segment.value}</span>
                                )
                              ))}
                            </div>
                          ) : (
                            <PrintAnswerCorrect>{part.filled || '-'}</PrintAnswerCorrect>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (question.type === 'essay' || question.type === 'audioEssay' || question.type === 'email') ? (
                  <PrintAnswer>
                    {answerData.studentAnswer ? (
                      <MessageRenderer data={answerData.studentAnswer} />
                    ) : (
                      <span>-</span>
                    )}
                  </PrintAnswer>
                ) : (question.type === 'video') ? (
                  <PrintAnswer>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '4px' }}>
                      {Boolean(answerData.hasVideoResponse) && (
                        <Videocam style={{ fontSize: '18px', color: '#666', flexShrink: 0 }} />
                      )}
                      <div style={{ flex: 1, lineHeight: '1.4' }}>
                        {answerData.transcript ? (
                          <MessageRenderer data={answerData.transcript} />
                        ) : (
                          <span>{answerData.studentAnswer || '-'}</span>
                        )}
                      </div>
                    </div>
                  </PrintAnswer>
                ) : (question.type === 'audio' || question.type === 'twoMinAudio') ? (
                  <PrintAnswer>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '4px' }}>
                      {Boolean(answerData.hasAudioResponse) && (
                        <AudioIconWrapper style={{ marginLeft: 0, flexShrink: 0 }}>
                          <Mic />
                        </AudioIconWrapper>
                      )}
                      <div style={{ flex: 1, lineHeight: '1.4' }}>
                        {answerData.transcript ? (
                          <MessageRenderer data={answerData.transcript} />
                        ) : (
                          <span>{answerData.studentAnswer || '-'}</span>
                        )}
                      </div>
                    </div>
                  </PrintAnswer>
                ) : answerData.isCorrect ? (
                  <PrintAnswerCorrect>{answerData.studentAnswer || '-'}</PrintAnswerCorrect>
                ) : (
                  <>
                    <PrintAnswerIncorrect showStrike={true}>{answerData.studentAnswer || '-'}</PrintAnswerIncorrect>
                    {answerData.showCorrect && answerData.correctAnswer && (
                      <PrintAnswerCorrect>{answerData.correctAnswer}</PrintAnswerCorrect>
                    )}
                  </>
                )}
              </PrintAnswerArea>
              {renderPrintAiForQuestion(item.index, aiGrades, aiFeedbacks, aiSuggestions)}
            </PrintSection>
          );
        })}
      </div>
    </PrintRoot>
  );
});

AssignmentPrintView.displayName = 'AssignmentPrintView';

export default AssignmentPrintView;
